import React, { useRef } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { rome as moment } from '../../utils/date';
import { openChat } from '../../store/brokers/partners/chats/ChatsUISlice';
import { useTheme } from '../../utils/theme-context';

export const ExecutiveTrips = ({
    items,
    hasNextPage,
    onShowMore,
    loadingMore,
    onOpenChat: parentOnOpenChat,
    chatData,
    chatUi,
    chatIsWaiting,
    onSendingData,
    hideHeader = false,
}: any) => {
    const dispatch = useAppDispatch();
	const { theme } = useTheme();
    const scrollerRef = useRef<HTMLDivElement | null>(null);

    const onOpenChat = async (tripId: string, journeyTitle?: string) => {
        try {
            dispatch(openChat({
                tripId: tripId,
                data: [],
                journeyTitle: journeyTitle,
            }));
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <div className='w-full space-y-3'>
            {!hideHeader && (
                <h3 className='m-0 text-lg font-semibold dark:text-primary-darkMode'>Active Trips</h3>
            )}
            {items?.length > 0 ?
                <div
                    ref={scrollerRef}
                    className='grid grid-flow-col auto-cols-[minmax(420px,516px)] gap-4 overflow-x-auto pb-2 [scroll-snap-type:x_mandatory]'
                >
                    {items?.map((t: any) => {
                        const title = t.itinerary?.journeyTitle ?? '';
                        const start = moment(t.startDate).format('DD MMM YYYY hh:mm A');
                        const end = moment(t.endDate).format('DD MMM YYYY hh:mm A');
                        const manager = t.trainManager?.username ?? '—';
                        const isActive = true;

                        return (
                            <div
                                key={t._id}
                                data-trip-card
                                className='flex flex-col justify-between bg-white dark:bg-bgColor-darkMode [scroll-snap-align:start]'
                                style={{
                                    borderRadius: '16px',
                                    borderWidth: '1px',
                                    borderColor: theme === 'dark' ? '#ffffff1a' : '#E5E7EB',
                                    maxWidth: '516px',
                                }}
                            >
                                {/* Header: Icon + Title */}
                                <div
                                    className='flex items-center gap-2 px-3'
                                    style={{
                                        backgroundColor: theme === 'dark' ? '#111317' : '#252B37',
                                        height: '60px',
                                        borderRadius: '16px 16px 0 0',
                                    }}
                                >
                                    <img src='/svg/directions_subway_white.svg' className='w-6 h-6 flex-shrink-0' alt='trip' />
                                    <span
                                        className='truncate dark:text-primary-darkMode'
                                        style={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontWeight: 500,
                                            fontSize: '18px',
                                            lineHeight: '100%',
                                            letterSpacing: '-0.02em',
                                            color: 'white',
                                        }}
                                        title={title}
                                    >
                                        {title}
                                    </span>
                                </div>

                                <div className='px-5 flex-1 flex flex-col justify-center' style={{ marginTop: '26px', marginBottom: '26px' }}>
                                    {/* Start / End row */}
                                    <div className='grid grid-cols-2 gap-x-4' style={{ marginBottom: '26px' }}>
                                        <div>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#717680',
                                                }}
                                            >
                                                Start:{' '}
                                            </span>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#252B37',
                                                }}
                                            >
                                                {start}
                                            </span>
                                        </div>
                                        <div>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#717680',
                                                }}
                                            >
                                                End:{' '}
                                            </span>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#252B37',
                                                }}
                                            >
                                                {end}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Train Manager + Status row */}
                                    <div className='grid grid-cols-2 gap-x-4'>
                                        <div>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#717680',
                                                }}
                                            >
                                                Train Manager:{' '}
                                            </span>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#252B37',
                                                }}
                                                title={manager}
                                            >
                                                {manager}
                                            </span>
                                        </div>
                                        <div className='flex items-center gap-1.5'>
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: theme === 'dark' ? '#fff' : '#717680',
                                                }}
                                            >
                                                Status:
                                            </span>
                                            <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                                            <span
                                                style={{
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: '14px',
                                                    lineHeight: '100%',
                                                    letterSpacing: '-0.02em',
                                                    color: isActive ? '#10B981' : theme === 'dark' ? '#fff' : '#71717A',
                                                }}
                                            >
                                                {isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Chat button styled as input */}
                                <div style={{ padding: '0 7px 7px 7px' }}>
                                    <div
                                        onClick={() => onOpenChat(t?._id || '', title)}
                                        className='flex items-center cursor-pointer hover:opacity-90 transition-opacity dark:text-[#717680]'
                                        style={{
                                            border: theme === 'dark' ? '2px solid #fff' : '2px solid #252B37',
                                            borderRadius: '16px',
                                            backgroundColor: theme === 'dark' ? '#111317' : '#ffffff',
                                            height: '60px',
                                            padding: '0 14px',
                                            width: '100%',
                                        }}
                                    >
                                        {/* Microphone icon */}
                                        <img src='/svg/brand_microphone.svg' className='flex-shrink-0 w-7 h-7 mr-3 dark:contrast-0' alt='mic' />

                                        {/* Placeholder text */}
                                        <span
                                            className='flex-1 select-none'
                                            style={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontWeight: 400,
                                                fontSize: '16px',
                                                lineHeight: '100%',
                                                letterSpacing: '-0.02em',
                                                color: theme === 'dark' ? '#717680' : '#1C1C1CCC',
                                            }}
                                        >
                                            Leave feedback
                                        </span>

                                        {/* Attach icon */}
                                        <img src='/svg/brand_attach.svg' className='flex-shrink-0 w-7 h-7 mr-2 dark:contrast-0' alt='attach' />

                                        {/* Send icon */}
                                        <img src='/svg/brand_send.svg' className='flex-shrink-0 w-4 h-[18px] dark:contrast-0' alt='send' />
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {hasNextPage && (
                        <div
                            data-trip-card
                            className='group flex items-center justify-center bg-white p-4 text-center shadow-sm [scroll-snap-align:start] cursor-pointer select-none hover:border-zinc-400'
                            style={{
                                borderRadius: '16px',
                                borderWidth: '1px',
                                borderStyle: 'dashed',
                                borderColor: '#D4D4D8',
                                minHeight: '112px',
                            }}
                            role='button'
                            tabIndex={0}
                            onClick={onShowMore}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onShowMore()}
                            aria-label='Show more trips'
                        >
                            <div className='flex flex-col items-center justify-center gap-2'>
                                {loadingMore ? (
                                    <>
                                        <svg className='h-5 w-5 animate-spin' viewBox='0 0 24 24' fill='none'>
                                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                                        </svg>
                                        <span className='text-sm text-zinc-700'>Loading…</span>
                                    </>
                                ) : (
                                    <span className='text-sm font-medium underline underline-offset-4 group-hover:opacity-80'>
                                        Show more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                :
                <div className='text-primary-light dark:text-primary-darkMode text-center my-3'>
                    No ongoing trips at this time.
                </div>
            }
        </div>
    );
};

export default ExecutiveTrips;
